<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useGame, useGamesStore } from '@nsftx/seven-client-retail-vue-sgp'
import { NXGDSButton, NXGDSButtonVariants } from '@nsftx/seven-nxg-design-system'
import { useShortcutsStore, getShortcutKeys } from '@nsftx/seven-nxg-retail-vue-sdk'
import { useWidgetsStore } from '@/stores/widgets/widgetsStore'
import SSShortcutKeys from '../../SSShortcutKeys.vue'

const { toggleShortcuts } = useWidgetsStore()
const { getGameShortcutsByDisplayId, getAppShortcutsByAction } = useShortcutsStore()
const { getGameLinkByDisplayId } = useGamesStore()
const route = useRoute<'game'>()
const router = useRouter()
const { t } = useI18n({ useScope: 'global' })

const gameLink = computed(() => getGameLinkByDisplayId(route.params.gameId))
const displayId = computed(() => useGame(gameLink.value).gameDisplayId.value)
const actionShortcut = computed(() => getAppShortcutsByAction('TOGGLE_SHORTCUTS')?.shortcut[0])
const actionShortcutKeys = computed(() =>
  actionShortcut.value ? getShortcutKeys(actionShortcut.value) : [],
)
const gameShortcuts = computed(() =>
  displayId.value ? getGameShortcutsByDisplayId(displayId.value) : [],
)

const navigateToShortcuts = () => {
  router.push({ name: 'keyboardShortcuts' })
}
</script>

<template>
  <div class="ss-shortcuts">
    <div class="ss-shortcuts__container ss-d-flex-column">
      <div class="ss-shortcuts__container__header">
        <div class="ss-shortcuts__container__header__left">
          <div class="ss-flex-center ss-gap-xs">
            <p class="ss-shortcuts__container__header__left__title">
              {{ t('widget_shortcuts_title') }}
            </p>
            <SSShortcutKeys v-if="actionShortcutKeys.length" :keys="actionShortcutKeys" />
          </div>
        </div>
        <div class="ss-shortcuts__container__header__right ss-d-flex">
          <NXGDSButton
            :variant="NXGDSButtonVariants.ghost"
            size="sm"
            prepend-icon="keyboard"
            @click="navigateToShortcuts"
          >
            {{ t('widget_shortcuts_all_shortcuts') }}
          </NXGDSButton>
          <NXGDSButton
            :variant="NXGDSButtonVariants.ghost"
            icon="close-a"
            size="sm"
            @click="toggleShortcuts"
          />
        </div>
      </div>

      <div v-if="gameShortcuts.length" class="ss-shortcuts__container__list ss-flex-wrap">
        <div
          v-for="gameShortcut in gameShortcuts"
          :key="gameShortcut.id"
          class="ss-shortcuts__container__list__item ss-flex-center"
        >
          <div class="ss-d-flex-column ss-gap-2xs">
            <SSShortcutKeys
              :keys="getShortcutKeys(gameShortcut.shortcut[0])"
              :label="gameShortcut.label"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@/styles/mixins' as mixins;

.ss-shortcuts {
  height: 100%;

  &__container {
    height: 100%;
    padding: var(--nxgds-alias-spacing-xs);
    border: 2px solid var(--nxgds-mapped-color-border-primary);
    border-radius: var(--nxgds-alias-radius-corner-md);

    &__header {
      display: flex;
      justify-content: space-between;
      padding: var(--nxgds-alias-spacing-xs) var(--nxgds-alias-spacing-2xs)
        var(--nxgds-alias-spacing-sm);
      color: var(--nxgds-mapped-color-text-secondary);
      @include mixins.typography('h4-medium');
    }

    &__list {
      gap: var(--nxgds-alias-spacing-sm) var(--nxgds-alias-spacing-lg);
    }
  }
}
</style>
